import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { parseCalendarDate } from "../utils";

const REPEAT_OPTIONS = ["daily", "weekly", "monthly", "yearly"];
const SHORT_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const FULL_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const titleCase = (value) => value.charAt(0).toUpperCase() + value.slice(1);

/* The task model stores monthly/yearly cadence as a `YYYY-MM-DD` string (it is
   concatenated with the time into `scheduleTime`), while the Lovable dialog
   picks a month + day of month. These two helpers bridge the pair. */
const parseScheduleDate = (value) => {
  const parsed = parseCalendarDate(value);
  if (!parsed) return { dayOfMonth: "", yearMonth: "" };
  return { dayOfMonth: parsed.day, yearMonth: MONTHS[parsed.monthIndex] };
};

const buildScheduleDate = (repeat, yearMonth, dayOfMonth) => {
  if (repeat !== "monthly" && repeat !== "yearly") return "";
  if (typeof dayOfMonth !== "number") return "";
  const year = new Date().getFullYear();
  const monthIndex =
    repeat === "yearly" ? MONTHS.indexOf(yearMonth) : new Date().getMonth();
  if (monthIndex < 0) return "";
  // The day select always offers 1-31, so clamp to the month's real length —
  // otherwise a 31st in February produced "2026-02-31", which no date parser
  // accepts and which broke both the summary and the next-run calculation.
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const month = String(monthIndex + 1).padStart(2, "0");
  const day = String(Math.min(dayOfMonth, daysInMonth)).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const CreateScheduleModal = ({
  open,
  initial,
  onClose,
  onCreate,
  isLive = false,
  onToggleLive,
}) => {
  // No existing cadence means we are creating one: every field starts empty and
  // the primary action stays locked until the schedule is valid.
  const isCreate = !initial;

  const [repeat, setRepeat] = useState("");
  const [every, setEvery] = useState("");
  const [weekDays, setWeekDays] = useState([]);
  const [dayOfMonth, setDayOfMonth] = useState("");
  const [yearMonth, setYearMonth] = useState("");
  const [time, setTime] = useState("");

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      setRepeat("");
      setEvery("");
      setWeekDays([]);
      setDayOfMonth("");
      setYearMonth("");
      setTime("");
      return;
    }
    const { dayOfMonth: initialDay, yearMonth: initialMonth } =
      parseScheduleDate(initial?.scheduleDate);
    setRepeat(
      REPEAT_OPTIONS.includes(initial?.repeat) ? initial.repeat : "daily",
    );
    setEvery(initial?.runCount || 1);
    setWeekDays(Array.isArray(initial?.weekDays) ? initial.weekDays : []);
    setDayOfMonth(initialDay);
    setYearMonth(initialMonth);
    setTime(initial?.time || "");
  }, [open, initial, isCreate]);

  const toggleWeekDay = (dayFull) =>
    setWeekDays((current) =>
      current.includes(dayFull)
        ? current.filter((existing) => existing !== dayFull)
        : [...current, dayFull],
    );

  const everyLabel =
    repeat === "weekly" ? "Weeks" : repeat === "monthly" ? "Months" : "Year(s)";

  const isScheduleValid =
    !!repeat &&
    !!time &&
    (repeat === "daily" || (typeof every === "number" && every >= 1)) &&
    (repeat !== "weekly" || weekDays.length > 0) &&
    (repeat !== "yearly" || !!yearMonth) &&
    (!(repeat === "monthly" || repeat === "yearly") ||
      typeof dayOfMonth === "number");

  const handleSave = () => {
    if (!isScheduleValid) return;
    onCreate?.({
      repeat,
      weekDays,
      scheduleDate: buildScheduleDate(repeat, yearMonth, dayOfMonth),
      runCount: repeat === "daily" ? 1 : every,
      time,
      // Consumed by the "Next run" calculation on the edit page.
      dayOfMonth,
      yearMonth,
    });
  };

  return (
    <Dialog
      open={Boolean(open)}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose?.();
      }}
    >
      <DialogContent className="max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-xl gap-0 overflow-y-auto p-0 sm:w-full">
        <div className="px-5 pb-1 pt-5 sm:px-8 sm:pt-6">
          <DialogHeader className="space-y-3 text-left">
            <span className="page-eyebrow">Scheduling</span>
            <DialogTitle className="page-display text-2xl font-normal leading-[1.05] md:text-[1.75rem]">
              {isCreate ? (
                <>
                  Create a <span className="accent">schedule</span>
                </>
              ) : (
                <>
                  Edit the <span className="accent">schedule</span>
                </>
              )}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Configure when this Task runs.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="space-y-2 px-5 pb-2 pt-3 sm:px-8">
          <div className="flex items-center gap-4 rounded-2xl border border-border/60 bg-muted/30 p-3">
            <button
              type="button"
              onClick={() => onToggleLive?.(!isLive)}
              className="relative h-6 w-11 shrink-0 rounded-full border border-border bg-background transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label="Toggle schedule status"
              aria-pressed={isLive}
            >
              <span
                className={`absolute top-0.5 h-5 w-5 rounded-full shadow transition-all ${
                  isLive ? "left-[22px] bg-emerald-600" : "left-0.5 bg-amber-500"
                }`}
              />
            </button>
            <div className="min-w-0">
              <p className="text-[15px] font-medium tracking-tight text-foreground">
                Status
              </p>
              <p
                className={`mt-1 text-sm font-light ${
                  isLive ? "text-emerald-600" : "text-amber-500"
                }`}
              >
                {isLive ? "Schedule live" : "Schedule paused"}
              </p>
            </div>
          </div>

          <div className="space-y-4 rounded-2xl border border-border/60 bg-muted/30 p-4">
            <div>
              <div className="page-eyebrow mb-2">Repeat</div>
              <div className="flex flex-wrap gap-2">
                {REPEAT_OPTIONS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setRepeat(option)}
                    className="interactive-chip"
                    data-active={option === repeat}
                  >
                    {titleCase(option)}
                  </button>
                ))}
              </div>
            </div>

            {!!repeat && repeat !== "daily" && (
              <div>
                <div className="page-eyebrow mb-3">
                  {repeat === "monthly" ? "Runs" : "Every"}
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min={1}
                    value={every}
                    onChange={(event) => {
                      const { value } = event.target;
                      setEvery(
                        value === "" ? "" : Math.max(1, Number(value) || 1),
                      );
                    }}
                    className="interactive-field h-10 w-16 rounded-md px-3 text-sm font-light text-foreground"
                  />
                  <span className="text-sm font-light text-muted-foreground">
                    {everyLabel}
                  </span>
                </div>
              </div>
            )}

            {repeat === "weekly" && (
              <div>
                <div className="page-eyebrow mb-3">Runs on</div>
                <div className="flex flex-wrap gap-2">
                  {SHORT_DAYS.map((short, index) => {
                    const fullName = FULL_DAYS[index];
                    return (
                      <button
                        key={short}
                        type="button"
                        onClick={() => toggleWeekDay(fullName)}
                        className="interactive-chip min-w-[56px] justify-center"
                        data-active={weekDays.includes(fullName)}
                      >
                        {short}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {repeat === "yearly" && (
              <div>
                <div className="page-eyebrow mb-3">Runs</div>
                <select
                  value={yearMonth}
                  onChange={(event) => setYearMonth(event.target.value)}
                  className="interactive-field h-10 w-44 rounded-md px-3 text-sm font-light text-foreground"
                >
                  <option value="" disabled>
                    Select month
                  </option>
                  {MONTHS.map((month) => (
                    <option key={month} value={month}>
                      {month}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {(repeat === "monthly" || repeat === "yearly") && (
              <div>
                {repeat === "monthly" && (
                  <div className="page-eyebrow mb-3">On</div>
                )}
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-foreground">
                    Day of month
                  </span>
                  <select
                    value={dayOfMonth}
                    onChange={(event) =>
                      setDayOfMonth(Number(event.target.value))
                    }
                    className="interactive-field h-10 w-20 rounded-md px-2 text-sm font-light text-foreground"
                  >
                    <option value="" disabled>
                      Select
                    </option>
                    {Array.from({ length: 31 }, (_, index) => index + 1).map(
                      (day) => (
                        <option key={day} value={day}>
                          {day}
                        </option>
                      ),
                    )}
                  </select>
                </div>
              </div>
            )}

            <div>
              <div className="page-eyebrow mb-2">Time</div>
              <div className="w-44">
                <input
                  type="time"
                  value={time}
                  onChange={(event) => setTime(event.target.value)}
                  className="interactive-field h-10 w-full rounded-md px-3 text-sm font-light text-foreground"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-1 flex flex-row items-center justify-end gap-2 border-t border-border/50 bg-muted/20 px-5 py-3 sm:justify-end sm:px-8">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-4 py-2 text-sm font-light text-muted-foreground transition-colors hover:text-foreground"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!isScheduleValid}
            className="rounded-md bg-foreground px-5 py-2.5 text-sm font-medium text-background shadow-sm transition-all hover:bg-foreground/90 active:scale-[0.98] disabled:bg-muted disabled:text-muted-foreground disabled:shadow-none disabled:opacity-60 disabled:active:scale-100"
          >
            {isCreate ? "Create schedule" : "Save schedule"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateScheduleModal;
